import React, { useState } from 'react';
import { Testimonial } from '../../services/api';
import useTestimonials from '../../hooks/useTestimonials';
import SkewedButton from '../common/SkewedButton';
import SkewedInput from '../common/SkewedInput';
import SkewedCard from '../common/SkewedCard';

const TestimonialForm: React.FC = () => {
  const { createTestimonial } = useTestimonials();
  const [formData, setFormData] = useState<Omit<Testimonial, 'id' | 'date' | 'approved'>>({
    name: '',
    role: '',
    content: '',
    rating: 5
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRatingChange = (rating: number) => {
    setFormData(prev => ({
      ...prev,
      rating
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    
    try {
      const result = await createTestimonial({
        ...formData,
        date: new Date()
      });
      
      if (result.success) {
        setSuccess(true);
        setFormData({
          name: '',
          role: '',
          content: '',
          rating: 5
        });
      } else {
        setError(result.error || 'Failed to submit testimonial');
      }
    } catch (err) {
      setError('An unexpected error occurred');
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <SkewedCard className="text-center" padding="p-8">
        <div className="text-green-500 text-4xl mb-4">✓</div>
        <h3 className="text-xl font-semibold mb-2">Thank You!</h3>
        <p className="text-gray-600 mb-4">
          Your testimonial has been submitted and is awaiting approval.
        </p>
        <SkewedButton
          onClick={() => setSuccess(false)}
          variant="secondary"
        >
          Submit another testimonial
        </SkewedButton>
      </SkewedCard>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
          Your Name <span className="text-red-500">*</span>
        </label>
        <SkewedInput
          id="name"
          name="name"
          type="text"
          required
          value={formData.name}
          onChange={handleChange}
          enableRandomSkew={false}
        />
      </div>

      <div>
        <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-2">
          Your Role/Company <span className="text-gray-400">(optional)</span>
        </label>
        <SkewedInput
          id="role"
          name="role"
          type="text"
          value={formData.role || ''}
          onChange={handleChange}
          placeholder="e.g. Homeowner, Business Owner, etc."
          enableRandomSkew={false}
        />
      </div>

      <div>
        <label htmlFor="rating" className="block text-sm font-medium text-gray-700 mb-2">
          Rating <span className="text-red-500">*</span>
        </label>
        <div id="rating" role="group" aria-label="Rating" className="flex space-x-1">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              onClick={() => handleRatingChange(star)}
              className="text-2xl focus:outline-none"
              aria-label={`${star} star${star !== 1 ? 's' : ''}`}
            >
              <span className={star <= formData.rating! ? 'text-yellow-400' : 'text-gray-300'}>
                ★
              </span>
            </button>
          ))}
        </div>
      </div>

      <div>
        <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-2">
          Your Testimonial <span className="text-red-500">*</span>
        </label>
        <SkewedInput
          as="textarea"
          id="content"
          name="content"
          rows={4}
          required
          value={formData.content}
          onChange={handleChange}
          placeholder="Share your experience working with me..."
          enableRandomSkew={false}
        />
      </div>

      {error && (
        <SkewedCard borderColor="red" backgroundColor="#fee" padding="p-3">
          <p className="text-red-500 text-sm">{error}</p>
        </SkewedCard>
      )}

      <div>
        <SkewedButton
          type="submit"
          disabled={submitting}
          variant="primary"
          className="w-full"
        >
          {submitting ? 'Submitting...' : 'Submit Testimonial'}
        </SkewedButton>
        <p className="text-xs text-gray-500 mt-2">
          Your testimonial will be reviewed before appearing on the site.
        </p>
      </div>
    </form>
  );
};

export default TestimonialForm;